import React, { PropsWithChildren } from 'react';
import { Paper } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import colors from '../../../../../../styles/colors';
import Grid from '@material-ui/core/Grid';
import AssignmentQuestionEditor from './question-type/AssignmentQuestionEditor';
import AssignmentPassageEditor from './question-type/AssignmentPassageEditor';
import {
  AssignmentData,
  AssignmentEditStatus,
  AssignmentQuestionFormat,
  AssignmentQuestionType
} from '../../state/assignment-edit.types';
import AssignmentEditorEmptyState from '../empty-state/AssignmentEditorEmptyState';
import QuestionRenderer from './question-renderer/QuestionRenderer';
import PassageRenderer from './question-renderer/PassageRenderer';

const useStyles = makeStyles({
  root: {
    border: `1px solid ${colors.assignment.grey}`
  },
  padding: {
    padding: '1.2rem'
  }
});

interface AssignmentEditorProps {
  data: AssignmentData;
  isEditMode: boolean;
  handleQuestionText(
    id: string,
    data: Partial<AssignmentData>,
    rootId?: string
  ): void;
  handleUpdateQuestionType(id: string, type: AssignmentQuestionType): void;
  handleQuestionFormat(id: string, format: number): void;
  handleAnswerUpdate(
    questionId: string,
    answerId: string,
    data: object,
    type?: AssignmentQuestionType,
    rootId?: string
  ): void;
  handleDeleteAnswer(
    id: string,
    answerId: string,
    type?: AssignmentQuestionType,
    rootId?: string
  ): void;
  handleAddAnswer(id: string, rootId?: string): void;
  handleAddQuestion(rootId?: string): void;
  handleQuestionIndex(id: string, rootId?: string): number;
  handleDeleteQuestion(id: string, rootId?: string): void;
  status: AssignmentEditStatus;
}
const AssignmentEditor: React.FC<PropsWithChildren<AssignmentEditorProps>> = (
  props: PropsWithChildren<AssignmentEditorProps>
) => {
  const {
    data,
    handleUpdateQuestionType,
    handleQuestionFormat,
    handleQuestionText,
    handleAnswerUpdate,
    handleDeleteAnswer,
    handleAddQuestion,
    handleQuestionIndex,
    handleDeleteQuestion,
    isEditMode,
    status
  } = props;
  const { type, id: questionId } = data;
  const classes = useStyles();

  const handleAddAnswer = (id: string, rootId?: string) => {
    props.handleAddAnswer(id, rootId);
  };

  const handleQuestionType = (type: AssignmentQuestionType) => {
    handleUpdateQuestionType(questionId, type);
  };

  const handleQuestionAnswerUpdate = (
    questionId: string,
    answerId: string,
    data: object,
    rootId?: string
  ) => {
    handleAnswerUpdate(questionId, answerId, data, type, rootId);
  };

  const handleAnswerDelete = (
    questionId: string,
    answerId: string,
    rootId?: string
  ) => {
    handleDeleteAnswer(questionId, answerId, type, rootId);
  };
  return (
    <Paper elevation={0} className={classes.root}>
      {!data.type ? (
        <AssignmentEditorEmptyState handleQuestionType={handleQuestionType} />
      ) : (
        <>
          {!isEditMode ? (
            <>
              {type === AssignmentQuestionType.Independent ? (
                <QuestionRenderer
                  handleDeleteQuestion={handleDeleteQuestion}
                  handleQuestionIndex={handleQuestionIndex}
                  question={data}
                />
              ) : (
                <PassageRenderer
                  handleDeleteQuestion={handleDeleteQuestion}
                  passageData={data}
                  handleQuestionIndex={handleQuestionIndex}
                />
              )}
            </>
          ) : (
            <Grid container>
              <Grid item xs={12} className={`${classes.padding}`}>
                {type === AssignmentQuestionType.Independent && (
                  <AssignmentQuestionEditor
                    showQuestionFormat={true}
                    handleDeleteAnswer={handleAnswerDelete}
                    handleQuestionFormat={handleQuestionFormat}
                    handleAddQuestionAnswer={handleAddAnswer}
                    handleAnswerUpdate={handleQuestionAnswerUpdate}
                    handleTextChange={handleQuestionText}
                    handleQuestionIndex={handleQuestionIndex}
                    handleDeleteQuestion={handleDeleteQuestion}
                    question={data}
                    status={status}
                  />
                )}
                {type === AssignmentQuestionType.Passage && (
                  <AssignmentPassageEditor
                    passageData={data}
                    handleAddQuestionAnswer={handleAddAnswer}
                    handleTextChange={handleQuestionText}
                    handleDeleteAnswer={handleAnswerDelete}
                    handleQuestionFormat={handleQuestionFormat}
                    handleAnswerUpdate={handleQuestionAnswerUpdate}
                    handleAddQuestion={handleAddQuestion}
                    handleQuestionIndex={handleQuestionIndex}
                    handleDeleteQuestion={handleDeleteQuestion}
                  />
                )}
              </Grid>
            </Grid>
          )}
        </>
      )}
    </Paper>
  );
};

export default AssignmentEditor;
